import type { StcConfig } from '@application/rh/StcConfig';
import type { AncienneteConfig } from '@application/rh/ConventionsCollectives';
import type { Conge } from '@shared/types/Conge';
import type { Salarie } from '@shared/types/Salarie';
import {
  ANCIENNETE_PLAFOND,
  ANCIENNETE_SEUIL_ANS,
  TAUX_PRIME_ANCIENNETE,
  TAUX_PRIME_ANCIENNETE_ANNUEL,
} from '@shared/constants/Taux';
import { roundFCFA } from '@shared/utils/round';
import { calculerSoldeCongesUnifie } from './CongesBalance';
import { ancienneteAnneesFractionnaires, preavisLegalParAnciennete } from './PreavisLegal';

export type MotifSortieStc = 'demission' | 'licenciement' | 'rupture' | 'fin_cdd';

export interface SoldeToutCompteLigne {
  libelle: string;
  base: string;
  montant: number;
}

export interface SoldeToutCompteResult {
  motif: MotifSortieStc;
  convention: string;
  base_mensuelle: number;
  conges_jours: number;
  indemnite_licenciement: number;
  indemnite_conges_payes: number;
  indemnite_precarite_cdd: number;
  indemnite_preavis: number;
  preavis_mois: number;
  treizieme_mois: number;
  anciennete_annees: number;
  lignes: SoldeToutCompteLigne[];
  total: number;
}

/** Barème général : 2 % après 2 ans puis +1 % par an, plafond 30 %. */
const DEFAULT_ANC: AncienneteConfig = {
  taux_par_an: TAUX_PRIME_ANCIENNETE_ANNUEL,
  debut_annee: ANCIENNETE_SEUIL_ANS,
  plafond: ANCIENNETE_PLAFOND,
  paliers: [{ annees: ANCIENNETE_SEUIL_ANS, taux: TAUX_PRIME_ANCIENNETE }],
};

function dateRefIso(salarie: Salarie): string {
  return salarie.date_sortie ?? new Date().toISOString().slice(0, 10);
}

export function ancienneteAnnees(dateEmbauche: string, dateSortie?: string): number {
  if (!dateEmbauche) return 0;
  const debut = new Date(dateEmbauche);
  const fin = dateSortie ? new Date(dateSortie) : new Date();
  return Math.max(0, (fin.getTime() - debut.getTime()) / (365.25 * 24 * 3600 * 1000));
}

export function tauxAncienneteStc(
  dateEmbauche: string,
  dateSortie?: string,
  ancienneteConfig?: AncienneteConfig | null,
): number {
  const ac = ancienneteConfig ?? DEFAULT_ANC;
  const debut = ac.debut_annee ?? DEFAULT_ANC.debut_annee;
  const taux = ac.taux_par_an ?? DEFAULT_ANC.taux_par_an;
  const plaf = ac.plafond ?? DEFAULT_ANC.plafond;
  // Années révolues (mois complets / 12) : 5 ans pile = 5, sans dérive des années bissextiles.
  const pleines = Math.floor(ancienneteAnneesFractionnaires(dateEmbauche, dateSortie));
  if (pleines < debut) return 0;
  const paliers = ac.paliers;
  if (!paliers?.length) return Math.min(taux * pleines, plaf);
  // Barème par paliers de la convention : dernier palier atteint, puis progression annuelle.
  let atteint: { annees: number; taux: number } | undefined;
  for (const p of paliers) if (p.annees <= pleines) atteint = p;
  if (!atteint) return 0;
  const dernier = paliers[paliers.length - 1]!;
  const t = atteint === dernier ? atteint.taux + taux * (pleines - atteint.annees) : atteint.taux;
  return Math.min(t, plaf);
}

export function baseStcMensuelle(
  salarie: Salarie,
  config: StcConfig,
  ancienneteConfig?: AncienneteConfig | null,
): number {
  const b = config.base;
  let total = 0;
  if (b.salaire_base !== false) total += salarie.salaire_base || 0;
  if (b.sursalaire) total += salarie.sursalaire || 0;
  if (b.prime_anciennete) {
    total += Math.round(
      (salarie.salaire_base || 0) *
        tauxAncienneteStc(salarie.date_embauche, dateRefIso(salarie), ancienneteConfig),
    );
  }
  if (b.transport) total += salarie.indemnite_transport || 0;
  if (b.logement) total += salarie.indemnite_logement || 0;
  if (b.fonction_ind) total += salarie.indemnite_fonction || 0;
  if (b.autres) total += salarie.indemnite_autres || 0;
  return roundFCFA(total);
}

function moisTravaillesAnnee(salarie: Salarie): number {
  const ref = dateRefIso(salarie);
  const moisSortie = parseInt(ref.slice(5, 7), 10) || 12;
  let moisDebut = 1;
  if (salarie.date_embauche && salarie.date_embauche.slice(0, 4) === ref.slice(0, 4)) {
    moisDebut = parseInt(salarie.date_embauche.slice(5, 7), 10) || 1;
  }
  return Math.max(1, Math.min(12, moisSortie - moisDebut + 1));
}

export function calculerSoldeCongesJours(
  salarie: Salarie,
  config: StcConfig,
  conges: readonly Conge[] = [],
): number {
  return calculerSoldeCongesUnifie(salarie, conges, config.conges).solde;
}

export function calculerIndemniteLicenciement(
  salarie: Salarie,
  config: StcConfig,
  ancienneteConfig?: AncienneteConfig | null,
): number {
  const lc = config.licenciement;
  // Art. 90 : les fractions d'année sont prises en considération (mois complets).
  let ans = ancienneteAnneesFractionnaires(salarie.date_embauche, salarie.date_sortie);
  if (ans < (lc.anciennete_min_annees || 0)) return 0;
  if (lc.annees_entieres) ans = Math.floor(ans);
  const base = baseStcMensuelle(salarie, config, ancienneteConfig);
  const tranches = lc.tranches?.length ? lc.tranches : [{ jusqua: 999, taux: 0.2 }];
  let prev = 0;
  let montant = 0;
  for (const tr of tranches) {
    const plafond = tr.jusqua;
    const anneesTranche = Math.max(0, Math.min(ans, plafond) - prev);
    if (anneesTranche > 0) montant += base * (tr.taux || 0) * anneesTranche;
    prev = plafond;
    if (ans <= plafond) break;
  }
  return roundFCFA(montant);
}

/**
 * Préavis en mois. Défaut : table légale par ancienneté (Code du travail art. 82, 15 jours = 0,5 mois).
 * Mode `categorie` : durées fixes par catégorie (option conventionnelle).
 */
export function calculerPreavisMois(salarie: Salarie, config: StcConfig): number {
  const p = config.preavis;
  if (p.mode !== 'categorie') {
    return preavisLegalParAnciennete(
      ancienneteAnneesFractionnaires(salarie.date_embauche, salarie.date_sortie),
    ).mois;
  }
  const cat = (salarie.categorie || '').toLowerCase();
  if (cat.includes('cadre') || cat.includes('direction')) return p.cadre ?? 3;
  if (cat.includes('maitrise') || cat.includes('maîtrise')) return p.maitrise ?? 2;
  return p.employe ?? 1;
}

export function calculerIndemniteCP(
  salarie: Salarie,
  config: StcConfig,
  conges: readonly Conge[],
  ancienneteConfig?: AncienneteConfig | null,
): number {
  const cc = config.conges;
  const jours = calculerSoldeCongesJours(salarie, config, conges);
  const base = baseStcMensuelle(salarie, config, ancienneteConfig);
  const baseJours = cc.base_jours || 30;
  return roundFCFA(jours * (base / baseJours));
}

export function calculerIndemnitePrecariteCDD(
  salarie: Salarie,
  config: StcConfig,
  ancienneteConfig?: AncienneteConfig | null,
  brutTotalOverride?: number,
): number {
  // Aucune prime de précarité en droit gabonais (Code du travail art. 60) : uniquement si une
  // convention ou le contrat la prévoit et que l'option est activée.
  if (!config.precarite_cdd.actif) return 0;
  const isCdd =
    salarie.statut_contrat === 'CDD' || /cdd/i.test(String(salarie.statut_contrat ?? ''));
  if (!isCdd) return 0;
  const taux = config.precarite_cdd.taux ?? 0;
  if (brutTotalOverride != null && brutTotalOverride > 0) {
    return roundFCFA(brutTotalOverride * taux);
  }
  if (!salarie.date_embauche || !salarie.date_fin_contrat) return 0;
  const [yE = 0, mE = 0] = salarie.date_embauche.slice(0, 7).split('-').map(Number);
  const [yF = 0, mF = 0] = salarie.date_fin_contrat.slice(0, 7).split('-').map(Number);
  const mois = Math.max(1, (yF - yE) * 12 + (mF - mE) + 1);
  return roundFCFA(baseStcMensuelle(salarie, config, ancienneteConfig) * mois * taux);
}

export function calculerTreiziemeProrata(salarie: Salarie, config: StcConfig): number {
  const tz = config.treizieme;
  if (!tz.actif) return 0;
  const base = (salarie.salaire_base || 0) + (salarie.sursalaire || 0);
  if (!tz.prorata) return roundFCFA(base);
  return roundFCFA(base * (moisTravaillesAnnee(salarie) / 12));
}

function formatPreavis(mois: number): string {
  if (mois < 1) return `${Math.round(mois * 30)} jours`;
  const arrondi = Math.round(mois * 100) / 100;
  return `${arrondi} mois`;
}

function buildLignes(
  salarie: Salarie,
  stc: Omit<SoldeToutCompteResult, 'lignes'>,
  congesManuel: boolean,
): SoldeToutCompteLigne[] {
  const lignes: SoldeToutCompteLigne[] = [
    {
      libelle: 'Salaire global mensuel de référence',
      base: 'Base de calcul',
      montant: stc.base_mensuelle,
    },
  ];
  if (stc.indemnite_licenciement > 0) {
    lignes.push({
      libelle: 'Indemnité de licenciement',
      base: 'Barème par ancienneté',
      montant: stc.indemnite_licenciement,
    });
  } else if (stc.motif === 'licenciement' || stc.motif === 'rupture') {
    lignes.push({
      libelle: 'Indemnité de licenciement',
      base: 'Barème par ancienneté',
      montant: 0,
    });
  }
  lignes.push({
    libelle: 'Indemnité compensatrice de congés payés',
    base: `${stc.conges_jours} j${congesManuel ? ' — saisi' : ' — auto'}`,
    montant: stc.indemnite_conges_payes,
  });
  lignes.push({
    libelle: 'Indemnité de précarité CDD',
    base: `${stc.indemnite_precarite_cdd > 0 ? 'Option conventionnelle' : '—'}`,
    montant: stc.indemnite_precarite_cdd,
  });
  lignes.push({
    libelle: 'Indemnité de préavis',
    base: `${formatPreavis(stc.preavis_mois)} — ${salarie.categorie || '—'}`,
    montant: stc.indemnite_preavis,
  });
  if (stc.treizieme_mois > 0) {
    lignes.push({
      libelle: '13e mois / gratification (prorata)',
      base: "Fin d'année",
      montant: stc.treizieme_mois,
    });
  }
  return lignes;
}

/**
 * Calcul du solde de tout compte (Code du travail, loi 022/2021 : art. 82, 87, 90, 222 ;
 * + paramètres entreprise et conventions).
 */
export class SoldeToutCompteService {
  static calculer(input: {
    salarie: Salarie;
    motif: MotifSortieStc;
    config: StcConfig;
    conges?: readonly Conge[];
    ancienneteConfig?: AncienneteConfig | null;
    brut_total_cdd?: number;
  }): SoldeToutCompteResult {
    const { salarie, motif, config } = input;
    const conges = input.conges ?? [];
    const ancienneteConfig = input.ancienneteConfig ?? null;
    const base_mensuelle = baseStcMensuelle(salarie, config, ancienneteConfig);
    const anciennete_annees = ancienneteAnnees(salarie.date_embauche, salarie.date_sortie);

    const indemnite_lic =
      motif === 'licenciement' || motif === 'rupture'
        ? calculerIndemniteLicenciement(salarie, config, ancienneteConfig)
        : 0;

    const conges_jours = calculerSoldeCongesJours(salarie, config, conges);
    const indemnite_cp = calculerIndemniteCP(salarie, config, conges, ancienneteConfig);

    const indemnite_precarite =
      motif === 'fin_cdd' || salarie.statut_contrat === 'CDD'
        ? calculerIndemnitePrecariteCDD(salarie, config, ancienneteConfig, input.brut_total_cdd)
        : 0;

    const preavis_mois = calculerPreavisMois(salarie, config);
    const preavisDu =
      (motif === 'licenciement' || motif === 'rupture') &&
      config.preavis.indemnise_si_non_effectue !== false;
    const indemnite_preavis = preavisDu ? roundFCFA(base_mensuelle * preavis_mois) : 0;

    const treizieme_mois = calculerTreiziemeProrata(salarie, config);

    const total = roundFCFA(
      indemnite_lic + indemnite_cp + indemnite_precarite + indemnite_preavis + treizieme_mois,
    );

    const congesManuel =
      typeof salarie.conges_solde === 'number' && !Number.isNaN(salarie.conges_solde);

    const core = {
      motif,
      convention: config.convention,
      base_mensuelle,
      conges_jours,
      indemnite_licenciement: indemnite_lic,
      indemnite_conges_payes: indemnite_cp,
      indemnite_precarite_cdd: indemnite_precarite,
      indemnite_preavis,
      preavis_mois,
      treizieme_mois,
      anciennete_annees,
      total,
    };

    return {
      ...core,
      lignes: buildLignes(salarie, core, congesManuel),
    };
  }
}
